import {
  Component,
  ChangeDetectionStrategy,
  input,
  signal,
  computed,
  effect,
  ElementRef,
  inject,
  OnDestroy,
  Directive,
  HostListener,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  Overlay,
  OverlayRef,
  OverlayModule,
  ConnectedPosition,
  FlexibleConnectedPositionStrategy,
} from '@angular/cdk/overlay';
import { ComponentPortal, TemplatePortal } from '@angular/cdk/portal';

/**
 * Tooltip Component - Rendered inside the overlay
 */
@Component({
  selector: 'app-tooltip',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div 
      class="tooltip-container px-3 py-2 bg-base-700 text-white text-xs rounded-md shadow-lg max-w-xs"
      [class]="tooltipClass()"
      role="tooltip">
      <div class="tooltip-content">{{ content() }}</div>
      @if (showArrow()) {
        <div class="tooltip-arrow" [class]="arrowClass()"></div>
      }
    </div>
  `,
  styles: [`
    :host {
      display: block;
    }
    .tooltip-container {
      position: relative;
      animation: tooltipFadeIn 0.15s ease-out;
    }
    @keyframes tooltipFadeIn {
      from { opacity: 0; transform: scale(0.95); }
      to { opacity: 1; transform: scale(1); }
    }
    .tooltip-arrow {
      position: absolute;
      width: 8px;
      height: 8px;
      background: inherit;
      transform: rotate(45deg);
    }
    .tooltip-arrow.arrow-bottom {
      bottom: -4px;
      left: 50%;
      margin-left: -4px;
    }
    .tooltip-arrow.arrow-top {
      top: -4px;
      left: 50%;
      margin-left: -4px;
    }
    .tooltip-arrow.arrow-left {
      left: -4px;
      top: 50%;
      margin-top: -4px;
    }
    .tooltip-arrow.arrow-right {
      right: -4px;
      top: 50%;
      margin-top: -4px;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TooltipComponent {
  content = input<string>('');
  tooltipClass = input<string>('');
  showArrow = input<boolean>(true);
  position = input<'top' | 'bottom' | 'left' | 'right'>('top');

  arrowClass = computed(() => {
    const pos = this.position();
    // Arrow points opposite to tooltip position
    switch (pos) {
      case 'top': return 'arrow-bottom';
      case 'bottom': return 'arrow-top';
      case 'left': return 'arrow-right';
      case 'right': return 'arrow-left';
      default: return 'arrow-bottom';
    }
  });
}

/**
 * Tooltip Directive - Attach to any element to show tooltip on hover
 * 
 * Usage:
 * <span appTooltip="This is tooltip text">Hover me</span>
 * <span [appTooltip]="dynamicText" tooltipPosition="bottom">Hover me</span>
 */
@Directive({
  selector: '[appTooltip]',
  standalone: true,
})
export class TooltipDirective implements OnDestroy {
  private overlay = inject(Overlay);
  private elementRef = inject(ElementRef);
  private viewContainerRef = inject(ViewContainerRef);
  
  private overlayRef: OverlayRef | null = null;
  private showTimeout: any;
  private hideTimeout: any;

  /** Tooltip content */
  appTooltip = input<string>('');
  
  /** Tooltip position: top, bottom, left, right */
  tooltipPosition = input<'top' | 'bottom' | 'left' | 'right'>('top');
  
  /** Delay before showing tooltip (ms) */
  tooltipDelay = input<number>(200);
  
  /** Delay before hiding tooltip (ms) */
  tooltipHideDelay = input<number>(0);
  
  /** Custom CSS class for tooltip */
  tooltipClass = input<string>('');
  
  /** Whether tooltip is disabled */
  tooltipDisabled = input<boolean>(false);

  @HostListener('mouseenter')
  onMouseEnter(): void {
    if (this.tooltipDisabled() || !this.appTooltip()) return;
    
    this.clearTimeouts();
    this.showTimeout = setTimeout(() => {
      this.show();
    }, this.tooltipDelay());
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    this.clearTimeouts();
    this.hideTimeout = setTimeout(() => {
      this.hide();
    }, this.tooltipHideDelay());
  }

  @HostListener('click')
  onClick(): void {
    // Hide on click (optional behavior)
    this.hide();
  }

  ngOnDestroy(): void {
    this.clearTimeouts();
    this.hide();
  }

  private show(): void {
    if (this.overlayRef) return;

    const positionStrategy = this.getPositionStrategy();
    
    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.close(),
      panelClass: 'tooltip-panel',
    });

    const portal = new ComponentPortal(TooltipComponent, this.viewContainerRef);
    const componentRef = this.overlayRef.attach(portal);
    
    // Set inputs on the tooltip component
    componentRef.setInput('content', this.appTooltip());
    componentRef.setInput('position', this.tooltipPosition());
    componentRef.setInput('tooltipClass', this.tooltipClass());
  }

  private hide(): void {
    if (this.overlayRef) {
      this.overlayRef.detach();
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }

  private clearTimeouts(): void {
    if (this.showTimeout) {
      clearTimeout(this.showTimeout);
      this.showTimeout = null;
    }
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }
  }

  private getPositionStrategy(): FlexibleConnectedPositionStrategy {
    const position = this.tooltipPosition();
    const positions = this.getPositions(position);

    return this.overlay
      .position()
      .flexibleConnectedTo(this.elementRef)
      .withPositions(positions)
      .withPush(true);
  }

  private getPositions(preferredPosition: string): ConnectedPosition[] {
    const positions: Record<string, ConnectedPosition[]> = {
      top: [
        { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -8 },
        { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: 8 },
      ],
      bottom: [
        { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: 8 },
        { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -8 },
      ],
      left: [
        { originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -8 },
        { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: 8 },
      ],
      right: [
        { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: 8 },
        { originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -8 },
      ],
    };

    return positions[preferredPosition] || positions['top'];
  }
}
